import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  getFirestore, doc, onSnapshot, collection, query, where, getDocs,
} from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Reservations.css';

const db = getFirestore();

const STATUS_CONTENT = {
  waiting: {
    icon: 'fa-hourglass-half',
    color: '#ff5722',
    headline: "You're on the waitlist",
    subtext: "We'll text you when your table is ready.",
  },
  notified: {
    icon: 'fa-bell',
    color: '#16a34a',
    headline: 'Your table is ready!',
    subtext: 'Please come to the host stand now.',
  },
  seated: {
    icon: 'fa-chair',
    color: '#2563eb',
    headline: "You're seated — enjoy!",
    subtext: 'Thanks for visiting.',
  },
  cancelled: {
    icon: 'fa-times-circle',
    color: '#dc2626',
    headline: 'Spot cancelled',
    subtext: 'Your spot in line was cancelled.',
  },
  no_show: {
    icon: 'fa-user-slash',
    color: '#7f1d1d',
    headline: 'Marked as no-show',
    subtext: "Ask the host to re-add you if you're still here.",
  },
};

/**
 * Live walk-in queue status — the deep-link target for the waitlist SMS
 * link (/queue/{entryId}?b={businessId}&l={locationId}). Shows position in
 * line, estimated wait, and live status updates.
 */
const QueueStatusPage = () => {
  const { entryId } = useParams();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('b') || '';
  const locationId = searchParams.get('l') || '';
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!businessId || !locationId || !entryId) {
      setLoading(false);
      return;
    }
    const ref = doc(db, 'users', businessId, 'locations', locationId, 'waitlist', entryId);
    const unsub = onSnapshot(ref, (snap) => {
      setEntry(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    }, (e) => {
      console.error('Queue status stream error:', e);
      setLoading(false);
    });
    return unsub;
  }, [businessId, locationId, entryId]);

  // Position in line (entries ahead, created before this one)
  useEffect(() => {
    const compute = async () => {
      if (!entry?.createdAt || !businessId) return;
      try {
        const q = query(
          collection(db, 'users', businessId, 'locations', entry.locationId, 'waitlist'),
          where('status', 'in', ['waiting', 'notified']),
          where('createdAt', '<', entry.createdAt)
        );
        const snap = await getDocs(q);
        setPosition(snap.size + 1);
      } catch (e) {
        console.warn('Position compute failed:', e);
      }
    };
    if (entry && ['waiting', 'notified'].includes(entry.status)) compute();
  }, [entry, businessId]);

  const content = entry ? (STATUS_CONTENT[entry.status] || STATUS_CONTENT.waiting) : null;

  return (
    <>
      <Header />
      <div className="ff-resv-page">
        {loading ? (
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading…</p>
        ) : !entry ? (
          <div className="ff-resv-card" style={{ textAlign: 'center', padding: 40 }}>
            <p>Waitlist entry not found.</p>
          </div>
        ) : (
          <>
            <div className="ff-resv-hero" style={{ background: `${content.color}18` }}>
              <i className={`fas ${content.icon}`} style={{ fontSize: 44, color: content.color }}></i>
              <h2 style={{ color: content.color }}>{content.headline}</h2>
              <p style={{ color: '#4b5563', margin: '8px 0 0' }}>
                {entry.status === 'notified' && entry.tableName
                  ? `Your table is ${entry.tableName} — please come to the host stand now.`
                  : content.subtext}
              </p>
            </div>

            {['waiting', 'notified'].includes(entry.status) && (
              <div className="ff-resv-card" style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#ff5722' }}>
                    {position != null ? `#${position}` : '…'}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>in line</div>
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#ff5722' }}>
                    ~{entry.quotedWaitMinutes || '?'} min
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>est. wait</div>
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#ff5722' }}>{entry.partySize}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>guests</div>
                </div>
              </div>
            )}

            <div className="ff-resv-card">
              <div className="ff-resv-detail-row">
                <i className="fas fa-utensils"></i>
                <span>{entry.locationName}</span>
              </div>
              {entry.createdAt?.toDate && (
                <div className="ff-resv-detail-row">
                  <i className="fas fa-clock"></i>
                  <span>Joined at {entry.createdAt.toDate().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                </div>
              )}
              <div className="ff-resv-detail-row">
                <i className="fas fa-user"></i>
                <span>{entry.customerName}</span>
              </div>
            </div>

            <p className="ff-resv-note">
              Keep this page open — it updates live. You'll also get an SMS when your table is ready.
            </p>
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default QueueStatusPage;
